import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [resetting, setResetting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/customer/verify-reset-token/${token}`
      );
      
      if (response.data.valid) {
        setTokenValid(true);
        setEmail(response.data.email);
      }
    } catch (error) {
      console.error('Erro ao verificar token:', error);
      setTokenValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      alert('As senhas não coincidem');
      return;
    }

    if (passwordData.new_password.length < 6) {
      alert('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setResetting(true);

    try {
      await axios.post(
        `${BACKEND_URL}/api/customer/reset-password`,
        {
          token: token,
          new_password: passwordData.new_password
        }
      );

      setSuccess(true);
      
      // Redirecionar para login após 3 segundos
      setTimeout(() => {
        navigate('/entrar');
      }, 3000);
    } catch (error) {
      console.error('Erro ao redefinir senha:', error);
      alert(error.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#00A86B]" />
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-md mx-auto px-4 py-16">
          <Card className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Link Inválido ou Expirado
            </h1>
            <p className="text-gray-600 mb-6">
              Este link de recuperação de senha é inválido ou já expirou.
              Por favor, solicite um novo link de recuperação.
            </p>
            <Button
              onClick={() => navigate('/entrar')}
              className="bg-[#00A86B] hover:bg-[#00A86B]/90"
            >
              Voltar para Login
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-md mx-auto px-4 py-16">
          <Card className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Senha Redefinida com Sucesso!
            </h1>
            <p className="text-gray-600 mb-6">
              Sua senha foi alterada com sucesso. Você será redirecionado para a página de login em instantes...
            </p>
            <Button
              onClick={() => navigate('/entrar')}
              className="bg-[#00A86B] hover:bg-[#00A86B]/90"
            >
              Ir para Login
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-md mx-auto px-4 py-16">
        <Card className="p-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-2 text-center">
            Redefinir Senha
          </h1>
          <p className="text-gray-600 mb-6 text-center">
            Digite sua nova senha para a conta <strong>{email}</strong>
          </p>

          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nova Senha
              </label>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                required
                minLength={6}
                placeholder="Digite sua nova senha"
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">Mínimo de 6 caracteres</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmar Nova Senha
              </label>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                required
                placeholder="Confirme sua nova senha"
                className="w-full"
              />
            </div>

            <Button
              type="submit"
              disabled={resetting}
              className="w-full bg-[#00A86B] hover:bg-[#00A86B]/90"
            >
              {resetting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Redefinindo...
                </>
              ) : (
                'Redefinir Senha'
              )}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
